import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import HomePage from "@/pages/HomePage";
import SolutionsPage from "@/pages/SolutionsPage";
import AccompagnementsPage from "@/pages/AccompagnementsPage";
import MethodePage from "@/pages/MethodePage";
import AProposPage from "@/pages/AProposPage";
import ContactPage from "@/pages/ContactPage";
import Cgv from '@/pages/Cgv';
import MentionsLegales from '@/pages/mentions-legales';
import ConnexionAlxioPage from '@/pages/ConnexionAlxioPage';
import ClientTestFormPage from '@/pages/ClientTestFormPage';
import RemerciementSignaturePage from '@/pages/RemerciementSignaturePage';
import FeedbackEmbedPage from '@/pages/FeedbackEmbedPage';
import ChatbottiaTestPage from '@/pages/ChatbottiaTestPage';
import TrelloSuiviCommercialPage from '@/pages/TrelloSuiviCommercialPage';
import AutopilotePage from '@/pages/AutopilotePage';
import PresentationAutopilotePage from '@/pages/PresentationAutopilotePage';
import MerciDemandeAutopilotePage from '@/pages/MerciDemandeAutopilotePage';
import { trackMetaCustomEvent, trackMetaEvent, trackRoutePageView, trackRouteViewContent } from '@/lib/metaPixel';
import { initGlobalUiSounds } from '@/lib/hoverSound';

function MetaPixelTracker() {
  const location = useLocation();
  const hasTrackedInitialPageView = useRef(false);

  useEffect(() => {
    if (hasTrackedInitialPageView.current) {
      trackRoutePageView(location.pathname);
    } else {
      hasTrackedInitialPageView.current = true;
    }

    trackRouteViewContent(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    const handleTrackedLinks = (event) => {
      if (!(event.target instanceof Element)) return;
      const anchor = event.target.closest('a[href]');
      if (!anchor) return;

      const href = anchor.getAttribute('href');
      if (!href) return;

      if (href.startsWith('tel:') || href.startsWith('mailto:')) {
        trackMetaEvent('Contact', {
          contact_method: href.startsWith('tel:') ? 'phone' : 'email',
          destination: href,
          page_path: window.location.pathname,
        });
      }

      let url;
      try {
        url = new URL(href, window.location.origin);
      } catch {
        return;
      }

      if (url.hostname.includes('cal.com') && anchor.dataset.localScheduleTracked !== 'true') {
        trackMetaEvent('Schedule', {
          destination: url.toString(),
          page_path: window.location.pathname,
        });
      }

      if (url.pathname === '/contact') {
        trackMetaCustomEvent('CTAContactClick', {
          page_path: window.location.pathname,
          destination: '/contact',
          cta_label: anchor.textContent?.trim() || 'unknown',
        });
      }
    };

    document.addEventListener('click', handleTrackedLinks, true);
    return () => {
      document.removeEventListener('click', handleTrackedLinks, true);
    };
  }, []);

  return null;
}


function App() {
  useEffect(() => {
    initGlobalUiSounds();
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <MetaPixelTracker />
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/solutions" element={<SolutionsPage />} />
          <Route path="/accompagnements" element={<AccompagnementsPage />} />
          <Route path="/methode" element={<MethodePage />} />
          <Route path="/a-propos" element={<AProposPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/conditions-generales-de-vente" element={<Cgv />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/connexion-alxio" element={<ConnexionAlxioPage />} />
          <Route path="/test-client-formulaire" element={<ClientTestFormPage />} />
          <Route path="/feedback-client" element={<FeedbackEmbedPage />} />
          <Route path="/test-chatbottia-client" element={<ChatbottiaTestPage />} />
          <Route path="/suivi-commercial-chatbottia" element={<TrelloSuiviCommercialPage />} />
          <Route path="/autopilote" element={<AutopilotePage />} />
          <Route path="/presentation-autopilote" element={<PresentationAutopilotePage />} />
          <Route path="/merci-demande-autopilote" element={<MerciDemandeAutopilotePage />} />
          <Route path="/remerciement-signature/" element={<RemerciementSignaturePage />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
